import React, { Suspense } from 'react';
import ErrorBoundary from './common/ErrorBoundary';
import Login from './Login';
import Home from './Home';
import Wizard from './Wizard';
import LocationSelection from './LocationSelection';
import ApplicantDashboard from './ApplicantDashboard';
import MonteurDashboard from './MonteurDashboard';
import AssetDashboard from './AssetDashboard';
import ExecutionView from './ExecutionView';
// Lazy loaded components
const AdminDashboard = React.lazy(() => import('./AdminDashboard'));
const ActiveLOTODashboard = React.lazy(() => import('./dashboard/ActiveLOTODashboard'));
const TechDocDashboard = React.lazy(() => import('./dashboard/TechDocDashboard'));
const KPIDashboard = React.lazy(() => import('./dashboard/KPIDashboard'));

// Fallback Spinner
const LoadingSpinner = () => (
    <div className="flex items-center justify-center min-h-screen bg-slate-900">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500"></div>
    </div>
);

export default function AppRouter({
    viewMode,
    setViewMode,
    adminMode,
    setAdminMode,
    user,
    // State & Setters
    selectedLocation,
    handleLocationSelect,
    editingPermit,
    setEditingPermit,
    executionPermit,
    setExecutionPermit,
    deepLinkTarget,
    setDeepLinkTarget,
    wizardSessionId,
    setWizardSessionId,
    storedScrollY,
    // Data
    wizardData,
    adminData,
    locationData,
    globalLibrary,
    taskMetadata,
    // Actions
    handleLogout,
    isPrivileged
}) {
    // Helper to render loading
    const Suspended = ({ children }) => (
        <Suspense fallback={<LoadingSpinner />}>{children}</Suspense>
    );

    // --- VIEW ROUTING ---

    if (viewMode === 'login') {
        return <Login />;
    }

    if (viewMode === 'home') {
        return (
            <Home
                currentUser={user}
                onLogout={handleLogout}
                onNavigate={(target) => {
                    if (target === 'wizard') {
                        setEditingPermit(null);
                        setDeepLinkTarget(null);
                        setAdminMode(false);
                        setViewMode('wizard');
                        setWizardSessionId(prev => prev + 1);
                    } else if (target === 'admin_dashboard') {
                        setAdminMode(true);
                        setViewMode('admin_dashboard');
                    } else if (target === 'active_loto_dashboard') {
                        setAdminMode(false);
                        setViewMode('active_loto_dashboard');
                    } else if (target === 'applicant_dashboard') {
                        setAdminMode(false);
                        setViewMode('applicant_dashboard');
                    } else if (target === 'monteur_dashboard') {
                        setAdminMode(false);
                        setViewMode('monteur_dashboard');
                    } else if (target === 'tech_doc_dashboard') {
                        setAdminMode(false);
                        setViewMode('tech_doc_dashboard');
                    }
                }}
            />
        );
    }

    if (viewMode === 'admin_dashboard') {
        return (
            <Suspended>
                <ErrorBoundary>
                    <AdminDashboard
                        currentUser={user}
                        onLogout={handleLogout}
                        onBack={() => {
                            setAdminMode(false);
                            setViewMode('home');
                        }}
                    />
                </ErrorBoundary>
            </Suspended>
        );
    }

    if (viewMode === 'active_loto_dashboard') {
        return (
            <div className="relative z-50">
                <Suspended>
                    <ErrorBoundary>
                        <ActiveLOTODashboard onBack={() => {
                            setViewMode('home');
                            if (adminMode) setAdminMode(false);
                        }} />
                    </ErrorBoundary>
                </Suspended>
            </div>
        );
    }

    if (viewMode === 'tech_doc_dashboard') {
        return (
            <div className="relative z-50">
                <Suspended>
                    <ErrorBoundary>
                        <TechDocDashboard onBack={() => {
                            setViewMode('home');
                            if (adminMode) setAdminMode(false);
                        }} />
                    </ErrorBoundary>
                </Suspended>
            </div>
        );
    }

    if (viewMode === 'kpi_dashboard') {
        return (
            <div className="relative z-50">
                <Suspended>
                    <ErrorBoundary>
                        <KPIDashboard
                            onBack={() => {
                                setViewMode('home');
                                if (adminMode) setAdminMode(false);
                            }}
                            onHome={() => setViewMode('home')}
                        />
                    </ErrorBoundary>
                </Suspended>
            </div>
        );
    }

    if (viewMode === 'applicant_dashboard') {
        return (
            <ErrorBoundary>
                <ApplicantDashboard
                    currentUser={user}
                    onBack={() => setViewMode('home')}
                    onEdit={(permit) => {
                        setEditingPermit(permit);
                        setViewMode('wizard');
                    }}
                />
            </ErrorBoundary>
        );
    }

    if (viewMode === 'monteur_dashboard') {
        return (
            <div className="relative">
                <ErrorBoundary>
                    <MonteurDashboard
                        currentUser={user}
                        onLogout={handleLogout}
                        onClose={() => {
                            setViewMode('home');
                            setExecutionPermit(null);
                        }}
                        onOpenExecution={setExecutionPermit}
                        users={adminData?.users || []}
                    />
                </ErrorBoundary>
            </div>
        );
    }

    if (viewMode === 'asset_dashboard' && deepLinkTarget) {
        return (
            <div className="relative">
                <ErrorBoundary>
                    <AssetDashboard
                        assetId={deepLinkTarget.assetId}
                        instanceName={deepLinkTarget.instanceName}
                        photoUrl={deepLinkTarget.photoUrl}
                        deepLinkLocation={{
                            hall: deepLinkTarget.hall,
                            line: deepLinkTarget.line,
                            subline: deepLinkTarget.subline
                        }}
                        location={selectedLocation}
                        hierarchyData={deepLinkTarget.overrides?.hierarchy || locationData?.hierarchy}
                        globalLibrary={deepLinkTarget.overrides?.library || globalLibrary}
                        taskMetadata={deepLinkTarget.overrides?.taskMetadata || taskMetadata}
                        currentUser={user}
                        onBack={() => {
                            setDeepLinkTarget(null);
                            if (!adminMode) {
                                setViewMode('wizard');
                            } else {
                                setTimeout(() => window.scrollTo(0, storedScrollY), 50);
                            }
                        }}
                        onStartWizard={(assetId) => {
                            setEditingPermit({
                                component: deepLinkTarget.assetId,
                                hall: deepLinkTarget.hall || '',
                                line: deepLinkTarget.line || '',
                                subline: deepLinkTarget.subline || '',
                                instanceName: deepLinkTarget.instanceName || '',
                                instancePhoto: deepLinkTarget.photoUrl || '',
                                taskImageUrls: deepLinkTarget.photoUrl ? [deepLinkTarget.photoUrl] : [],
                                isNewFromAsset: true
                            });
                            setViewMode('wizard');
                            setAdminMode(false);
                            setWizardSessionId(prev => prev + 1);
                        }}
                    />
                </ErrorBoundary>
            </div>
        );
    }

    if (executionPermit) {
        return (
            <ErrorBoundary>
                <ExecutionView
                    permit={executionPermit}
                    onClose={() => setExecutionPermit(null)}
                    currentUser={user}
                />
            </ErrorBoundary>
        );
    }

    // Default / Wizard View
    // This is essentially "else if (viewMode === 'wizard' || (viewMode === 'home' which is handled above) ... )"
    // Usually if nothing matches we might want to return <Home /> or null, 
    // but the original app has a specific structure for the Wizard being "always there" but hidden/shown.
    // However, to make it a true router, we should return the Wizard logic here.

    // NOTE: The original App.jsx had local Wizard header logic mixed in. 
    // Ideally we extract that too. For now let's reproduce the Wizard render.

    if (viewMode === 'wizard' && !adminMode) {
        return (
            <div style={{ display: 'block' }}>
                <header className="glass-panel border-x-0 border-t-0 rounded-none py-2 md:py-4 mb-4 md:mb-8 no-print sticky top-0 z-40">
                    <div className="container flex flex-col md:flex-row items-center justify-between gap-4 md:gap-8">
                        {/* ... Header Logic is complex, ideally passed as children or part of Wizard layout... 
                             For simplicity, I will include the simplified header here or just the Wizard component 
                             and assume the Header is part of the Wizard "Page".
                             
                             Wait, extracting the header logic is big. 
                             Let's render the Wizard directly and accept simpler header for now or copy it.
                         */}

                        {/* REPLICATION OF HEADER FOR WIZARD CONTEXT */}
                        <div className="flex items-center gap-4 w-full md:w-auto justify-between md:justify-start">
                            <div>
                                <h1 className="text-lg md:text-xl font-bold tracking-tight text-white">RaconWorks</h1>
                                <p className="text-[10px] md:text-xs text-slate-400">
                                    {selectedLocation} | {user.username || user.email}
                                </p>
                            </div>
                        </div>

                        <div className="flex gap-2">
                            <button onClick={() => setViewMode('home')} className="btn btn-sm bg-slate-700 text-white">🏠 Home</button>
                            <button onClick={handleLogout} className="btn btn-sm bg-slate-800 text-white">Uitloggen</button>
                        </div>
                    </div>
                </header>

                <main className="container">
                    <ErrorBoundary>
                        <Wizard
                            key={`${wizardSessionId}-${selectedLocation || 'default'}`}
                            data={wizardData}
                            locationName={selectedLocation}
                            currentUser={user}
                            initialData={editingPermit}
                            availableLocations={(() => {
                                if (adminData?.locations) return Object.keys(adminData.locations);
                                if (user.allowedLocations) return user.allowedLocations;
                                return [];
                            })()}
                            onUpdateLocation={handleLocationSelect}
                        />
                    </ErrorBoundary>
                </main>
            </div>
        );
    }

    // Fallback
    return null;
}
